/**
 * Settings → Workflow Configuration → Workflow Engine → **builder**
 * (full-page detail view, Standing rule 12 / BO UI Standard §4).
 *
 * THREE COLUMNS: Workflow Nodes (palette) · the flow (centre) · Node
 * Configuration. The centre column carries the flow and offers it two ways —
 * Diagram (default) and Steps — because the two answer different needs: the
 * diagram shows the shape at a glance, the step list is faster to reorder, and
 * 15 of the 24 seeded workflows are purely linear.
 *
 * Both views edit the SAME data. The diagram can be dragged to reposition
 * nodes; STRUCTURE (order, branches, QC attachment) is owned by the step model,
 * so the two views can never disagree about what the workflow does. Node
 * positions are cosmetic and never reach the engine.
 *
 * The workflow is still STORED as a graph — `graph.ts` translates both ways, so
 * nothing about the storage model changes and the two views can never disagree.
 *
 * Anatomy required by the BO UI Standard and honoured here:
 *   · header — "{BO} – {record}" title, breadcrumb, prev/next record arrows
 *   · action bar rendered at TOP and BOTTOM, identical sets
 *   · the Save trio — Save · Save & New · Save & Close · Cancel — all through
 *     the shared useSaveFlow (confirm → audited handler → "Saved successfully.")
 *   · required-yet-empty fields take the yellow tint + red asterisk
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSaveFlow, useConfirm } from '../../components/confirm';
import { confirmDiscardIfUnsaved, useUnsavedGuard } from '../../hooks/useUnsavedGuard';
import { useHotkeys, formatCombo, type HotkeyDef } from '../../components/hotkeys';
import { useNotify } from '../../components/notifications';
import { CheckedCombo } from '../../components/CheckedCombo';
import { createWorkflow, getWorkflow, listScopeOptions, listWorkflows, updateWorkflow } from './api';
import {
  CAT_BY_TYPE, catalogFor, fieldsForTab, isPrepFamily, paletteGroups,
  type CatEntry, type CatField, type FieldTab,
} from './catalog';
import { flatten, linearize, materialize, type Step } from './graph';
import type { WorkflowEdge as WorkflowEdgeShape, WorkflowNode as WorkflowNodeShape } from './types';
import { WORKFLOW_MODULES, type WorkflowListRow, type WorkflowModuleId } from './types';
import './workflow.css';

interface Props {
  /** Existing workflow to open; null = create a new one. */
  oid: string | null;
  moduleId: WorkflowModuleId;
  /**
   * How the record opens. Double-clicking a row opens it **read-only** so a
   * workflow can be inspected without any risk of altering a published
   * configuration by accident; Edit (from the list toolbar, or the Edit button
   * here) is the deliberate act that makes it editable. New always opens in
   * edit mode. Defaults to 'view'.
   */
  initialMode?: 'view' | 'edit';
  /** Sibling ids in the current list, for the prev/next record arrows. */
  siblingIds?: string[];
  onNavigate?: (oid: string) => void;
  onClose: () => void;
  onSavedNew?: () => void;
}

/** Canvas card width — shared by the layout maths and the edge endpoints. */
const NODE_W = 186;

let tmpSeq = 0;
const newKey = () => `tmp${++tmpSeq}`;

/** A blank step of a given type, seeded from the catalog defaults. */
function blankStep(entry: CatEntry): Step {
  return {
    key: newKey(),
    type: entry.type,
    name: entry.label,
    props: { ...entry.defaults },
    qc: [],
  };
}

export default function WorkflowBuilderPage({
  oid, moduleId, initialMode = 'view', siblingIds = [], onNavigate, onClose, onSavedNew,
}: Props) {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const { confirm } = useConfirm();

  const [name, setName]       = useState('');
  const [code, setCode]       = useState('');
  const [desc, setDesc]       = useState('');
  const [test, setTest]       = useState('');
  const [matrix, setMatrix]   = useState('');
  const [method, setMethod]   = useState('');
  const [label, setLabel]     = useState('');
  const [steps, setSteps]     = useState<Step[]>([]);
  const [selKey, setSelKey]   = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  /** Which view the CENTRE column shows. Defaults to the diagram: the flow is
   *  what the screen is about, and burying it cost two review rounds. */
  const [centreView, setCentreView] = useState<'steps' | 'diagram'>('diagram');
  const [status, setStatus]   = useState('Draft');
  const [version, setVersion] = useState(1);
  /** New records are always editable; existing ones open as `initialMode` says. */
  const [mode, setMode]       = useState<'view' | 'edit'>(oid ? initialMode : 'edit');
  /** Set by any edit, so leaving with unsaved work asks first. */
  const [dirty, setDirty]     = useState(false);
  // Registered with the shared guard so a sidebar click, Sign Out or a tab close asks too - not just
  // this page's own Back / Cancel ("Unsaved changes will be lost. Do you want to proceed?").
  useUnsavedGuard(dirty);
  /** Bumped to force a re-fetch (e.g. after discarding edits). */
  const [reloadKey, setReloadKey] = useState(0);
  /** Closed lists for the scope-key pickers, from the live Tests master data. */
  const [testOpts, setTestOpts] = useState<string[]>([]);
  const [methodOpts, setMethodOpts] = useState<string[]>([]);
  const [matrixOpts, setMatrixOpts] = useState<string[]>([]);
  const [optsLoading, setOptsLoading] = useState(false);
  /** Existing workflows in this module, offered as a starting point on New. */
  const [copyOpts, setCopyOpts] = useState<WorkflowListRow[]>([]);
  const [copying, setCopying] = useState(false);
  /** Node Configuration tab — General / Rules / Execution. */
  const [propTab, setPropTab] = useState<FieldTab>('general');

  const readOnly = mode === 'view';

  const meta = WORKFLOW_MODULES[moduleId];
  const palette = useMemo(() => catalogFor(moduleId), [moduleId]);

  /** Load an existing workflow, or seed a new one with just a Start step. */
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!oid) {
        const start = palette.find(p => p.type === 'start');
        setName(''); setCode(''); setDesc('');
        setTest(''); setMatrix(''); setMethod(''); setLabel('');
        setSteps(start ? [blankStep(start)] : []);
        setStatus('Draft'); setVersion(1); setSelKey(null);
        return;
      }
      setLoading(true);
      try {
        const wf = await getWorkflow(oid);
        if (cancelled || !wf) return;
        setName(wf.workflowName); setCode(wf.workflowCode ?? ''); setDesc(wf.description ?? '');
        setTest(wf.scopeTest ?? ''); setMatrix(wf.scopeMatrix ?? '');
        setMethod(wf.scopeMethod ?? ''); setLabel(wf.scopeLabel ?? '');
        setStatus(wf.status); setVersion(wf.version);
        const { steps: s, orphans } = linearize(wf.nodes, wf.edges);
        setSteps(s);
        setSelKey(s[0]?.key ?? null);
        // Never silently drop part of a graph the builder cannot express.
        if (orphans.length) {
          notify.warning(`${orphans.length} component(s) in this workflow are not part of the main sequence and are not shown in the step list. Saving from here would remove them.`);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void run();
    return () => { cancelled = true; };
  }, [oid, palette, notify, reloadKey]);

  /** Opening a different record resets the mode + dirty flag. */
  useEffect(() => { setMode(oid ? initialMode : 'edit'); setDirty(false); }, [oid, initialMode]);

  /** Load the Test picker's closed list once — only the modules that key off a
   *  Test need it. A failure leaves the list empty rather than blocking the
   *  form; the existing value still displays. */
  useEffect(() => {
    if (moduleId !== 'prep' && moduleId !== 'analysis') return;
    let cancelled = false;
    setOptsLoading(true);
    Promise.all([listScopeOptions('test'), listScopeOptions('method'), listScopeOptions('matrix')])
      .then(([t, m, x]) => { if (!cancelled) { setTestOpts(t); setMethodOpts(m); setMatrixOpts(x); } })
      .catch(() => { if (!cancelled) { setTestOpts([]); setMethodOpts([]); setMatrixOpts([]); } })
      .finally(() => { if (!cancelled) setOptsLoading(false); });
    return () => { cancelled = true; };
  }, [moduleId]);

  /** Workflows already configured in this module — the "Copy from" list.
   *  Only needed when creating; an existing record has nothing to clone into. */
  useEffect(() => {
    if (oid) return;
    let cancelled = false;
    listWorkflows(moduleId)
      .then(v => { if (!cancelled) setCopyOpts(v); })
      .catch(() => { if (!cancelled) setCopyOpts([]); });
    return () => { cancelled = true; };
  }, [oid, moduleId]);

  /**
   * Test is a SINGLE scope value (`AI_Workflow.ScopeTest`), so the combo is
   * used as a one-of picker: ticking a different test replaces the current one
   * rather than accumulating. The value already on the record is always kept in
   * the option list, so an existing workflow never loses its Test just because
   * that test has since been retired from the master data.
   */
  const testOptions = useMemo(() => {
    const set = new Set(testOpts);
    if (test.trim()) set.add(test.trim());
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [testOpts, test]);

  const onTestChange = useCallback((values: string[]) => {
    const next = values.find(v => v !== test) ?? values[0] ?? '';
    setDirty(true);
    setTest(next);
  }, [test]);

  /** Same one-of picker treatment for Method (`AI_Workflow.ScopeMethod`). */
  const methodOptions = useMemo(() => {
    const set = new Set(methodOpts);
    if (method.trim()) set.add(method.trim());
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [methodOpts, method]);

  const onMethodChange = useCallback((values: string[]) => {
    const next = values.find(v => v !== method) ?? values[0] ?? '';
    setDirty(true);
    setMethod(next);
  }, [method]);

  /** Matrix — the third part of the prep scope key, same closed-list treatment. */
  const matrixOptions = useMemo(() => {
    const set = new Set(matrixOpts);
    if (matrix.trim()) set.add(matrix.trim());
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [matrixOpts, matrix]);

  const onMatrixChange = useCallback((values: string[]) => {
    const next = values.find(v => v !== matrix) ?? values[0] ?? '';
    setDirty(true);
    setMatrix(next);
  }, [matrix]);

  /**
   * Copy from — clone an existing workflow's PROCESS as the starting point for
   * a new one. This is how a new method usually gets configured: it is a
   * variation on one the lab already runs.
   *
   * The steps, their properties and the description are copied; the SCOPE KEY
   * (Test / Matrix / Method) and the FlowID deliberately are NOT. Two workflows
   * sharing a scope key would both match the same samples and one would shadow
   * the other, so the new flow must be given its own scope explicitly.
   *
   * Every copied step gets a FRESH key, so the clone is independent of the
   * source rather than a set of references back into it.
   */
  const copyFrom = useCallback(async (sourceOid: string) => {
    if (!sourceOid) return;
    setCopying(true);
    try {
      const wf = await getWorkflow(sourceOid);
      if (!wf) return;
      const { steps: s } = linearize(wf.nodes, wf.edges);
      const rekey = (list: Step[]): Step[] => list.map(x => ({
        ...x,
        key: newKey(),
        qc: rekey(x.qc),
        branch: x.branch?.map(b => ({ ...b, steps: rekey(b.steps) })),
        x: undefined, y: undefined,          // let the auto-layout place the clone
      }));
      setSteps(rekey(s));
      setDesc(wf.description ?? '');
      setSelKey(null);
      setDirty(true);
      notify.success(`Copied ${flatten(s).length} steps from “${wf.workflowName}”. Give the new flow its own Test and FlowID.`);
    } catch {
      notify.error('Could not copy that workflow.');
    } finally {
      setCopying(false);
    }
  }, [notify]);

  // ── step-tree helpers ───────────────────────────────────────────────────
  /** Immutably map over the whole tree, including branches and QC children. */
  const mapTree = useCallback((list: Step[], fn: (s: Step) => Step): Step[] =>
    list.map(s => fn({
      ...s,
      qc: s.qc.map(q => fn(q)),
      branch: s.branch?.map(b => ({ ...b, steps: mapTree(b.steps, fn) })),
    })), []);

  const findStep = useCallback((list: Step[], key: string): Step | null => {
    for (const s of list) {
      if (s.key === key) return s;
      for (const q of s.qc) if (q.key === key) return q;
      for (const b of s.branch ?? []) {
        const hit = findStep(b.steps, key);
        if (hit) return hit;
      }
    }
    return null;
  }, []);

  const selected = useMemo(
    () => (selKey ? findStep(steps, selKey) : null), [steps, selKey, findStep]);

  const updateSelected = useCallback((patch: Partial<Step>) => {
    if (!selKey || readOnly) return;
    setDirty(true);
    setSteps(prev => mapTree(prev, s => (s.key === selKey ? { ...s, ...patch } : s)));
  }, [selKey, mapTree, readOnly]);

  const setProp = useCallback((key: string, value: unknown) => {
    if (!selKey || readOnly) return;
    setDirty(true);
    setSteps(prev => mapTree(prev, s =>
      s.key === selKey ? { ...s, props: { ...s.props, [key]: value } } : s));
  }, [selKey, mapTree, readOnly]);

  const addStep = useCallback((entry: CatEntry) => {
    const step = blankStep(entry);
    if (entry.type === 'qc') {
      // QC attaches to the selected step's batch rather than joining the chain.
      if (!selected || selected.type === 'qc') {
        notify.warning('Select the preparation or analysis step this QC belongs to first — QC attaches to that step’s batch.');
        return;
      }
      setSteps(prev => mapTree(prev, s =>
        s.key === selected.key ? { ...s, qc: [...s.qc, step] } : s));
      return;
    }
    setSteps(prev => [...prev, step]);
    setSelKey(step.key);
  }, [selected, mapTree, notify]);

  const move = useCallback((key: string, dir: -1 | 1) => {
    setSteps(prev => {
      const i = prev.findIndex(s => s.key === key);
      if (i < 0) return prev;
      const j = i + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      const a = next[i], b = next[j];
      if (!a || !b) return prev;
      next[i] = b; next[j] = a;
      return next;
    });
  }, []);

  const removeStep = useCallback((key: string) => {
    const strip = (list: Step[]): Step[] => list
      .filter(s => s.key !== key)
      .map(s => ({
        ...s,
        qc: s.qc.filter(q => q.key !== key),
        branch: s.branch?.map(b => ({ ...b, steps: strip(b.steps) })),
      }));
    setSteps(prev => strip(prev));
    if (selKey === key) setSelKey(null);
  }, [selKey]);

  /** Turn the selected step into a branch by giving it Yes/No sub-lists. */
  const addBranch = useCallback(() => {
    const dec = palette.find(p => p.type === 'decision');
    if (!dec) return;
    const step = blankStep(dec);
    step.branch = [
      { port: 'yes', label: 'YES', steps: [] },
      { port: 'no',  label: 'NO',  steps: [] },
    ];
    setSteps(prev => [...prev, step]);
    setSelKey(step.key);
    notify.info('Branch added — add the steps that follow on each path, then anything after the branch rejoins automatically.');
  }, [palette, notify]);

  const addToBranch = useCallback((decisionKey: string, port: string, entry: CatEntry) => {
    const step = blankStep(entry);
    setSteps(prev => mapTree(prev, s => (s.key === decisionKey
      ? { ...s, branch: s.branch?.map(b => b.port === port ? { ...b, steps: [...b.steps, step] } : b) }
      : s)));
    setSelKey(step.key);
  }, [mapTree]);

  // ── validation + save ───────────────────────────────────────────────────
  const problems = useMemo(() => {
    const out: string[] = [];
    if (!name.trim()) out.push('Workflow name is required.');
    if ((moduleId === 'prep' || moduleId === 'analysis') && !test.trim())
      out.push('Test is required — it is the scope key routing resolves on.');
    const all = flatten(steps);
    if (!all.some(s => s.type === 'start')) out.push('The workflow needs a Start step.');
    const need: Record<string, [string, string]> = {
      prep: ['prep', 'A Sample Preparation workflow needs at least one Preparation step.'],
      analysis: ['analysis', 'An Analysis workflow needs at least one Analysis step.'],
      sampling: ['collect', 'A Sampling workflow must collect at least one container.'],
      registration: ['register', 'A Registration workflow must register the sample.'],
      reporting: ['deliver', 'A Reporting workflow must deliver the output somewhere.'],
    };
    const req = need[moduleId];
    if (req && !all.some(s => s.type === req[0])) out.push(req[1]);
    if (moduleId === 'prep') {
      const tiers = [...new Set(all.filter(s => s.type === 'prep')
        .map(s => Number(s.props.tier) || 1))].sort((a, b) => a - b);
      if (tiers.length && tiers[0] !== 1) out.push('Preparation must start at Tier 1.');
    }
    for (const s of all) {
      const cat = CAT_BY_TYPE[s.type];
      for (const f of cat?.fields ?? []) {
        if (f.required && (s.props[f.key] === '' || s.props[f.key] == null))
          out.push(`“${s.name}” — ${f.label} is required.`);
      }
    }
    return out;
  }, [name, test, steps, moduleId]);

  const buildPayload = useCallback((publish: boolean) => {
    const { nodes, edges } = materialize(steps);
    return {
      moduleId, workflowName: name.trim(), workflowCode: code.trim() || null,
      description: desc.trim() || null,
      scopeTest: test.trim() || null, scopeMatrix: matrix.trim() || null,
      scopeMethod: method.trim() || null, scopeLabel: label.trim() || null,
      nodes, edges, publish,
    };
  }, [steps, moduleId, name, code, desc, test, matrix, method, label]);

  const doSave = useCallback((after: 'stay' | 'new' | 'close', publish = false) => {
    if (problems.length) {
      void confirm({
        title: 'Configuration Incomplete',
        message: `Fix the following before saving:\n\n• ${problems.join('\n• ')}`,
        tone: 'danger', confirmLabel: 'OK',
      });
      return;
    }
    void runSave({
      formName: 'Workflow Configuration',
      auditAction: oid ? 'edit' : 'create',
      save: async () => {
        const body = buildPayload(publish);
        if (oid) await updateWorkflow(oid, body);
        else await createWorkflow(body);
      },
      onSaved: () => {
        if (after === 'close') onClose();
        else if (after === 'new') onSavedNew?.();
      },
    });
  }, [problems, confirm, runSave, oid, buildPayload, onClose, onSavedNew]);

  /** Return to the list — the ONE way back, offered in both modes. Unsaved
   *  work is confirmed first rather than silently discarded. */
  const backToList = useCallback(() => {
    void confirmDiscardIfUnsaved().then(ok => { if (ok) onClose(); });   // the ONE shared prompt
  }, [onClose]);

  /** Cancel: an existing record drops back to read-only; a new one leaves. */
  const cancelEdit = useCallback(() => {
    if (!oid) { backToList(); return; }
    if (!dirty) { setMode('view'); return; }
    void confirmDiscardIfUnsaved().then(ok => {
      if (!ok) return;
      setDirty(false);
      setMode('view');
      setReloadKey(k => k + 1);   // re-fetch so the discarded edits are really gone
    });
  }, [oid, dirty, backToList]);

  // ── prev / next record ──────────────────────────────────────────────────
  const idx = oid ? siblingIds.indexOf(oid) : -1;
  const goPrev = () => { const p = idx > 0 ? siblingIds[idx - 1] : undefined; if (p) onNavigate?.(p); };
  const goNext = () => { const n = idx >= 0 ? siblingIds[idx + 1] : undefined; if (n) onNavigate?.(n); };

  // Keyboard shortcuts (CLAUDE.md standing rule 14, §18.6, ADR-0013): the same
  // detail-view set the shared <SettingsDetailPage> binds, since this bespoke
  // full-page builder plays that role (BO UI Standard §4).  View mode → Edit
  // (alt+e) + Back to list (Esc); edit mode → the Save trio (Save mod+s · Save &
  // New mod+shift+s · Save & Close mod+enter) + Cancel (Esc); the prev/next
  // record arrows are bound in both.  Each mirrors its button's handler + disabled
  // state; Esc is allowInInput:false so it never fires while typing in a field.
  // Publish has no reserved key and is left unbound.  Registered UNCONDITIONALLY
  // (this component has no early return) — the ARRAY is gated by mode, not the call.
  const wfKeys: HotkeyDef[] = [];
  if (readOnly) {
    wfKeys.push({ combo: 'alt+e', label: 'Edit', group: 'Workflow', order: 1, run: () => setMode('edit') });
    wfKeys.push({ combo: 'esc', label: 'Back to list', group: 'Workflow', order: 9, allowInInput: false, run: () => backToList() });
  } else {
    wfKeys.push({ combo: 'mod+s', label: 'Save', group: 'Workflow', order: 1, run: () => doSave('stay') });
    wfKeys.push({ combo: 'mod+shift+s', label: 'Save & New', group: 'Workflow', order: 2, run: () => doSave('new') });
    wfKeys.push({ combo: 'mod+enter', label: 'Save & Close', group: 'Workflow', order: 3, run: () => doSave('close') });
    wfKeys.push({ combo: 'esc', label: 'Cancel', group: 'Workflow', order: 9, allowInInput: false, run: () => cancelEdit() });
  }
  wfKeys.push({ combo: 'alt+left', label: 'Previous record', group: 'Workflow', order: 7, disabled: idx <= 0, run: () => goPrev() });
  wfKeys.push({ combo: 'alt+right', label: 'Next record', group: 'Workflow', order: 8, disabled: idx < 0 || idx >= siblingIds.length - 1, run: () => goNext() });
  useHotkeys(wfKeys);

  // ── render helpers ──────────────────────────────────────────────────────
  const renderField = (f: CatField, s: Step) => {
    const v = s.props[f.key];
    const empty = v === '' || v == null;
    const cls = `wf-in${f.required && empty ? ' wf-req' : ''}`;
    if (f.type === 'bool') {
      return (
        <label className="wf-check">
          <input type="checkbox" checked={Boolean(v)} disabled={readOnly}
            onChange={e => setProp(f.key, e.target.checked)} />
          <span>{f.label}</span>
        </label>
      );
    }
    if (f.type === 'select') {
      return (
        <select className={cls} value={String(v ?? '')} disabled={readOnly}
          onChange={e => setProp(f.key, e.target.value)}>
          <option value="">—</option>
          {f.options?.map(o => <option key={String(o)} value={String(o)}>{String(o)}</option>)}
        </select>
      );
    }
    return (
      <input
        className={cls}
        type={f.type === 'number' ? 'number' : 'text'}
        value={String(v ?? '')}
        readOnly={readOnly}
        onChange={e => setProp(f.key, f.type === 'number' ? Number(e.target.value) : e.target.value)}
      />
    );
  };

  /** The one detail worth showing per component type, from a raw prop bag —
   *  shared by the step rows and the canvas cards so they never disagree. */
  const detailOfProps = (type: string, p: Record<string, unknown>, tier?: number | null): string => {
    if (isPrepFamily(type) && (p.tier || tier)) return `${p.prepType ?? 'Preparation'} · Tier ${p.tier ?? tier}`;
    if (type === 'analysis' && p.entryMode) return String(p.entryMode);
    if (type === 'qc' && p.qcType) return String(p.qcType);
    if (type === 'qceval' && p.scope) return String(p.scope);
    if (type === 'review' && p.level) return String(p.level);
    if (type === 'reagent' && p.reagent) return String(p.reagent);
    if (type === 'sdms' && p.template) return String(p.template);
    if (type === 'calc' && p.target) return String(p.target);
    if (type === 'deliver' && p.channel) return String(p.channel);
    if (type === 'route' && p.mode) return String(p.mode);
    if (type === 'decision' && p.field) return `${p.field} ${p.op ?? ''} ${p.value ?? ''}`.trim();
    if (type === 'end' && p.deliverable) return String(p.deliverable);
    if (type === 'start' && p.trigger) return String(p.trigger);
    return '';
  };

  /** The one detail worth showing inline per component type. */
  const detailOf = (s: Step): string => {
    const p = s.props;
    if (isPrepFamily(s.type) && p.tier) return ` · Tier ${p.tier}`;
    if (s.type === 'analysis' && p.entryMode) return ` · ${p.entryMode}`;
    if (s.type === 'qc' && p.qcType) return ` · ${p.qcType}`;
    if (s.type === 'qceval' && p.scope) return ` · ${p.scope}`;
    if (s.type === 'review' && p.level) return ` · ${String(p.level).split(' ')[0]}`;
    if (s.type === 'reagent' && p.reagent) return ` · ${p.reagent}`;
    if (s.type === 'sdms' && p.template) return ` · ${p.template}`;
    if (s.type === 'calc' && p.target) return ` · ${p.target}`;
    if (s.type === 'deliver' && p.channel) return ` · ${p.channel}`;
    if (s.type === 'route' && p.mode) return ` · ${p.mode}`;
    if (s.type === 'end' && p.deliverable) return ` · ${p.deliverable}`;
    return '';
  };

  const StepRow = ({ s, depth, index, total, parentKey }:
    { s: Step; depth: number; index?: number; total?: number; parentKey?: string }) => {
    const cat = CAT_BY_TYPE[s.type];
    const on = selKey === s.key;
    return (
      <>
        <div
          className={`wf-step${on ? ' active' : ''}`
            + (s.type === 'start' ? ' wf-step-first' : '')
            + (s.type === 'end' ? ' wf-step-last' : '')
            + (parentKey ? ' wf-step-child' : '')}
          style={{ paddingLeft: 10 + depth * 22 }}
          onClick={() => setSelKey(s.key)}
        >
          {/* Two-letter badge, as on the palette — a step is recognisable at a
              glance without reading the type line. */}
          <span className="wf-badge" style={{ background: cat?.colour }}>{cat?.badge ?? '?'}</span>
          <span className="wf-step-body">
            <span className="wf-step-name">{s.name}</span>
            <span className="wf-step-meta">
              {cat?.label}
              {detailOf(s)}
              {/* Role — who performs this step. Drives work queues and
                  segregation of duties, so it belongs on the row, not buried. */}
              {s.props.role ? <span className="wf-role">{String(s.props.role)}</span> : null}
            </span>
          </span>
          {/* Reorder / remove exist only while editing — a read-only view must
              not offer a control that silently does nothing. */}
          {!readOnly && !parentKey && (
            <span className="wf-step-actions" onClick={e => e.stopPropagation()}>
              <button className="wf-mini" title="Move up" disabled={index === 0}
                onClick={() => move(s.key, -1)}><i className="ti ti-chevron-up" /></button>
              <button className="wf-mini" title="Move down" disabled={index === (total ?? 1) - 1}
                onClick={() => move(s.key, 1)}><i className="ti ti-chevron-down" /></button>
              <button className="wf-mini wf-mini-del" title="Remove"
                onClick={() => removeStep(s.key)}><i className="ti ti-trash" /></button>
            </span>
          )}
          {!readOnly && parentKey && (
            <span className="wf-step-actions" onClick={e => e.stopPropagation()}>
              <button className="wf-mini wf-mini-del" title="Remove"
                onClick={() => removeStep(s.key)}><i className="ti ti-trash" /></button>
            </span>
          )}
        </div>

        {/* QC attached to this step's batch */}
        {s.qc.map(q => <StepRow key={q.key} s={q} depth={depth + 1} parentKey={s.key} />)}

        {/* Branch lanes */}
        {s.branch?.map(b => (
          <div key={b.port}>
            <div className="wf-branch-hdr" style={{ paddingLeft: 10 + (depth + 1) * 22 }}>
              {b.label}
            </div>
            {b.steps.map(bs => <StepRow key={bs.key} s={bs} depth={depth + 2} parentKey={s.key} />)}
            {!readOnly && (
              <div className="wf-branch-add" style={{ paddingLeft: 10 + (depth + 2) * 22 }}>
                <select className="wf-in wf-in-sm" value=""
                  onChange={e => {
                    const entry = palette.find(p => p.type === e.target.value);
                    if (entry) addToBranch(s.key, b.port, entry);
                  }}>
                  <option value="">＋ Add step to {b.label}…</option>
                  {palette.filter(p => p.type !== 'start').map(p =>
                    <option key={p.type} value={p.type}>{p.label}</option>)}
                </select>
              </div>
            )}
            {readOnly && b.steps.length === 0 && (
              <div className="wf-branch-empty" style={{ paddingLeft: 10 + (depth + 2) * 22 }}>
                (goes straight to the next step)
              </div>
            )}
          </div>
        ))}
      </>
    );
  };

  /** Back to list is ALWAYS first, in both modes — it is the only way out and
   *  must be obvious. Read-only shows just Back + Edit; editing shows the
   *  Save trio + Publish + Cancel. */
  const ActionBar = () => (
    <div className="wf-actions">
      <button className="ca-btn wf-back" onClick={backToList}>
        <i className="ti ti-arrow-left" /> Back to list
      </button>
      <span className="wf-actions-sep" />

      {readOnly ? (
        <>
          <button className="ca-btn ca-btn-solid" onClick={() => setMode('edit')}
            title={`Edit (${formatCombo('alt+e')})`}>
            <i className="ti ti-pencil" /> Edit
          </button>
          <span className="wf-readonly-note">
            <i className="ti ti-lock" /> Read-only — press Edit to change this workflow
          </span>
        </>
      ) : (
        <>
          <button className="ca-btn ca-btn-solid" onClick={() => doSave('stay')}
            title={`Save (${formatCombo('mod+s')})`}>
            <i className="ti ti-device-floppy" /> Save
          </button>
          <button className="ca-btn" onClick={() => doSave('new')}
            title={`Save & New (${formatCombo('mod+shift+s')})`}>
            <i className="ti ti-plus" /> Save &amp; New
          </button>
          <button className="ca-btn" onClick={() => doSave('close')}
            title={`Save & Close (${formatCombo('mod+enter')})`}>
            <i className="ti ti-check" /> Save &amp; Close
          </button>
          <span className="wf-actions-sep" />
          <button className="ca-btn" onClick={() => doSave('stay', true)}>
            <i className="ti ti-rocket" /> Publish
          </button>
          <button className="ca-btn" onClick={cancelEdit}
            title={`Cancel (${formatCombo('esc')})`}>
            <i className="ti ti-x" /> Cancel
          </button>
          {problems.length > 0 && (
            <span className="wf-problems">{problems.length} item(s) to fix</span>
          )}
        </>
      )}
    </div>
  );

  const diagram = useMemo(() => materialize(steps), [steps]);

  /** Persist a dragged node's position onto its step (cosmetic only). */
  const moveNode = useCallback((key: string, x: number, y: number) => {
    setDirty(true);
    setSteps(prev => mapTree(prev, s => (s.key === key ? { ...s, x, y } : s)));
  }, [mapTree]);

  /** Put every node back on the auto-layout by clearing stored positions. */
  const autoArrange = useCallback(() => {
    setDirty(true);
    setSteps(prev => mapTree(prev, s => {
      const { x: _x, y: _y, ...rest } = s;
      return rest as Step;
    }));
    notify.info('Nodes re-arranged automatically.');
  }, [mapTree, notify]);

  /**
   * The flow picture — and, in edit mode, DRAGGABLE. Selecting a node here
   * selects it in the step list too, so the diagram is a real view of the same
   * thing rather than a screenshot.
   *
   * Dragging moves a node only; it never changes the graph. Structure
   * (order, branches, QC attachment) stays owned by the Steps view, so the two
   * can never disagree about what the workflow actually does.
   */
  const renderDiagram = (g: { nodes: WorkflowNodeShape[]; edges: WorkflowEdgeShape[] }) => {
    const w = NODE_W + 80 + g.nodes.reduce((m, n) => Math.max(m, n.posX), 0);
    const h = 120 + g.nodes.reduce((m, n) => Math.max(m, n.posY), 0);

    const onNodeDown = (e: React.MouseEvent, n: WorkflowNodeShape) => {
      setSelKey(n.nodeKey);
      if (readOnly) return;
      e.preventDefault();
      const svg = (e.currentTarget as SVGGElement).ownerSVGElement;
      if (!svg) return;
      const start = svg.getBoundingClientRect();
      const dx = e.clientX - start.left - n.posX;
      const dy = e.clientY - start.top - n.posY;
      const onMove = (ev: MouseEvent) => {
        const nx = Math.max(0, Math.round((ev.clientX - start.left - dx) / 10) * 10);
        const ny = Math.max(0, Math.round((ev.clientY - start.top - dy) / 10) * 10);
        moveNode(n.nodeKey, nx, ny);
      };
      const onUp = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    };

    return (
      <svg width={Math.max(640, w)} height={Math.max(260, h)}>
        {g.edges.map((e, i) => {
          const a = g.nodes.find(n => n.nodeKey === e.fromNodeKey);
          const b = g.nodes.find(n => n.nodeKey === e.toNodeKey);
          if (!a || !b) return null;
          const qc = CAT_BY_TYPE[b.nodeType]?.type === 'qc';
          return (
            <g key={i}>
              <line x1={a.posX + NODE_W} y1={a.posY + 22} x2={b.posX} y2={b.posY + 22}
                stroke="#9fb0c4" strokeWidth={1.5}
                strokeDasharray={qc ? '4 3' : undefined} />
              {e.portKey !== 'out' && (
                <text x={a.posX + 176} y={a.posY + 16} fontSize={10} fontWeight={600}
                  fill={e.portKey === 'yes' || e.portKey === 'pass' ? '#2e9e5b' : '#d0645a'}>
                  {e.portKey.toUpperCase()}
                </text>
              )}
            </g>
          );
        })}
        {/* Node CARD: a tinted header band carrying the badge + name, then a
            body of up to three lines — type, the one detail that matters, and
            Role. Matches the reviewed designer; the previous 2-line box hid
            Role entirely, which is the field that says who does the work. */}
        {g.nodes.map(n => {
          const cat = CAT_BY_TYPE[n.nodeType];
          const on = selKey === n.nodeKey;
          const colour = cat?.colour ?? '#9fb0c4';
          const props = (() => { try { return JSON.parse(n.propsJson ?? '{}'); } catch { return {}; } })();
          const detail = detailOfProps(n.nodeType, props, n.prepTier);
          const role = props.role ? `Role: ${props.role}` : null;
          const lines = [cat?.label, detail, role].filter(Boolean) as string[];
          const H = 34 + lines.length * 15 + 8;
          return (
            <g key={n.nodeKey}
               style={{ cursor: readOnly ? 'pointer' : 'grab' }}
               onMouseDown={e => onNodeDown(e, n)}>
              {/* card */}
              <rect x={n.posX} y={n.posY} width={NODE_W} height={H} rx={5}
                fill="#fff" stroke={on ? colour : '#d7dce4'} strokeWidth={on ? 2.5 : 1} />
              {/* header band, tinted with the component's own colour */}
              <rect x={n.posX} y={n.posY} width={NODE_W} height={30} rx={5} fill={colour} opacity={0.12} />
              <rect x={n.posX} y={n.posY + 25} width={NODE_W} height={5} fill={colour} opacity={0.12} />
              {/* badge */}
              <rect x={n.posX + 8} y={n.posY + 7} width={20} height={16} rx={3} fill={colour} />
              <text x={n.posX + 18} y={n.posY + 19} fontSize={9.5} fontWeight={700}
                fill="#fff" textAnchor="middle">{cat?.badge}</text>
              <text x={n.posX + 34} y={n.posY + 20} fontSize={12} fontWeight={700} fill="#1f2430">
                {(n.nodeName ?? '').slice(0, 20)}
              </text>
              {/* body lines */}
              {lines.map((l, i) => (
                <text key={i} x={n.posX + 12} y={n.posY + 46 + i * 15} fontSize={10.5} fill="#6b7280">
                  {l.slice(0, 28)}
                </text>
              ))}
            </g>
          );
        })}
      </svg>
    );
  };

  return (
    <div className="bl-wrap wf-builder">
      {/* ── header ──────────────────────────────────────────────────────── */}
      <div className="wf-hdr">
        <div>
          <div className="wf-crumb">
            <a onClick={onClose}>Workflow Configuration</a> / {meta?.label ?? moduleId} /{' '}
            <b>{name || 'New workflow'}</b>
          </div>
          <h2 className="section-title">
            <i className={`ti ${meta?.icon ?? 'ti-sitemap'}`} />
            Workflow – {name || 'New workflow'}
            <span className="wf-pill">{status} v{version}</span>
          </h2>
        </div>
        <div className="wf-recnav">
          <button className="ca-btn" disabled={idx <= 0} onClick={goPrev}
            title={`Previous record (${formatCombo('alt+left')})`}>
            <i className="ti ti-chevron-left" />
          </button>
          <button className="ca-btn" disabled={idx < 0 || idx >= siblingIds.length - 1}
            onClick={goNext} title={`Next record (${formatCombo('alt+right')})`}>
            <i className="ti ti-chevron-right" />
          </button>
        </div>
      </div>

      <ActionBar />

      {/* ── identity + scope ────────────────────────────────────────────── */}
      <div className="bl-card wf-form">
        {/* Copy from — only when creating. A new method is usually a variation
            on one the lab already runs, so starting from that beats building
            the same eight steps again. */}
        {!oid && copyOpts.length > 0 && (
          <div className="wf-copyfrom">
            <span className="wf-copyfrom-ic"><i className="ti ti-copy" /></span>
            <div className="wf-copyfrom-body">
              <label htmlFor="wf-copy-src">Copy from an existing flow</label>
              <span className="wf-copyfrom-hint">
                Copies the steps only — scope and FlowID stay blank.
              </span>
            </div>
            <select
              id="wf-copy-src"
              className="wf-in wf-copyfrom-sel"
              defaultValue=""
              disabled={copying}
              onChange={e => { void copyFrom(e.target.value); e.target.value = ''; }}
            >
              <option value="">
                {copying ? 'Copying…' : `Choose a ${meta?.label ?? ''} flow…`}
              </option>
              {copyOpts.map(w => (
                <option key={w.oid} value={w.oid}>
                  {w.workflowCode ? `${w.workflowCode} — ` : ''}{w.workflowName}
                  {w.nodeCount ? ` (${w.nodeCount} nodes)` : ''}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="wf-grid">
          <label>Workflow Name <b className="wf-star">*</b></label>
          <input className={`wf-in${!name.trim() && !readOnly ? ' wf-req' : ''}`} value={name} readOnly={readOnly}
            onChange={e => setName(e.target.value)} />
          <label>FlowID</label>
          {/* System-assigned, never typed. FlowID is a human-readable
              SEQUENTIAL id, minted race-safe in AI_Workflow_Save_SP via
              dbo.AI_GenerateSequentialId + a UNIQUE index (CLAUDE.md 14a
              Rule B). Letting it be edited here would reintroduce exactly the
              duplicate-ID defect that rule exists to prevent. */}
          <input className="wf-in" value={code || (oid ? '' : '(assigned on save)')} readOnly
            title="Assigned automatically when the workflow is first saved" />

          <label>Description</label>
          <input className="wf-in" value={desc} readOnly={readOnly}
            onChange={e => { setDirty(true); setDesc(e.target.value); }} />
          <label>Module</label>
          <input className="wf-in" value={meta?.label ?? moduleId} readOnly />

          {(moduleId === 'prep' || moduleId === 'analysis') ? (
            <>
              <label>Test <b className="wf-star">*</b></label>
              {/* Closed list from the live Tests master data — a workflow must
                  never be scoped to a test that does not exist, because that
                  value is what routing resolves on. */}
              <CheckedCombo
                options={testOptions}
                values={test.trim() ? [test.trim()] : []}
                onChange={onTestChange}
                placeholder="Select a test…"
                required={!test.trim()}
                disabled={readOnly}
                loading={optsLoading}
                ariaLabel="Test"
                unit="test"
              />
              <label>Method</label>
              {/* Closed list, same as Test — Method is part of the scope key
                  routing resolves on, so a typo makes a workflow that silently
                  never matches. */}
              <CheckedCombo
                options={methodOptions}
                values={method.trim() ? [method.trim()] : []}
                onChange={onMethodChange}
                placeholder="Select a method…"
                disabled={readOnly}
                loading={optsLoading}
                ariaLabel="Method"
                unit="method"
              />
              {moduleId === 'prep' && (
                <>
                  <label>Matrix</label>
                  <CheckedCombo
                    options={matrixOptions}
                    values={matrix.trim() ? [matrix.trim()] : []}
                    onChange={onMatrixChange}
                    placeholder="Select a matrix…"
                    disabled={readOnly}
                    loading={optsLoading}
                    ariaLabel="Matrix"
                    unit="matrix"
                  />
                  <span /><span />
                </>
              )}
            </>
          ) : (
            <>
              <label>Applies to</label>
              <input className="wf-in" value={label} readOnly={readOnly}
                onChange={e => { setDirty(true); setLabel(e.target.value); }}
                placeholder={meta?.scopeLabel} />
              <span /><span />
            </>
          )}
        </div>
      </div>

      {/* ── THREE COLUMNS: node palette · flow · node configuration ────────
          Restructured 2026-08-14 to the reviewed Method Workflow Designer
          layout. Previously the palette was a dropdown under the step list and
          the diagram sat in a collapsed card at the BOTTOM of the page, so the
          flow — the thing the screen is about — was the least visible element.
          The centre column now owns the flow and can show it either as the
          editable Steps list or as the Diagram, without either being buried. */}
      <div className="wf-build3">

        {/* ── LEFT · Workflow Nodes ───────────────────────────────────────── */}
        <aside className="bl-card wf-palette">
          <div className="wf-side-hdr">Workflow Nodes</div>
          <div className="wf-palette-list">
            {paletteGroups(moduleId).map(([group, entries]) => (
              <div key={group}>
                <div className="wf-palette-grp">{group.toUpperCase()}</div>
                {entries.map(p => (
                  <button
                    key={p.type}
                    type="button"
                    className="wf-palette-item"
                    disabled={readOnly}
                    title={readOnly ? 'Read-only — press Edit to change this workflow' : `Add ${p.label}`}
                    onClick={() => addStep(p)}
                  >
                    <span className="wf-badge" style={{ background: p.colour }}>{p.badge}</span>
                    <span className="wf-palette-txt">
                      <span className="wf-palette-name">{p.label}</span>
                      <span className="wf-palette-desc">{p.desc}</span>
                    </span>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </aside>

        {/* ── CENTRE · the flow ───────────────────────────────────────────── */}
        <section className="bl-card wf-centre">
          <div className="wf-side-hdr wf-centre-hdr">
            <span>
              {centreView === 'steps' ? 'Steps' : 'Diagram'}
              <span className="wf-side-count"> ({flatten(steps).length} nodes)</span>
            </span>
            <span className="wf-centre-tools">
              {centreView === 'diagram' && !readOnly && (
                <button type="button" className="ca-btn wf-mini-btn" onClick={autoArrange}
                  title="Re-run the automatic layout, discarding any manual positions">
                  <i className="ti ti-layout-grid" /> Auto Arrange
                </button>
              )}
              <span className="wf-viewtoggle">
                <button type="button" className={centreView === 'steps' ? 'active' : ''}
                  onClick={() => setCentreView('steps')}>
                  <i className="ti ti-list-numbers" /> Steps
                </button>
                <button type="button" className={centreView === 'diagram' ? 'active' : ''}
                  onClick={() => setCentreView('diagram')}>
                  <i className="ti ti-sitemap" /> Diagram
                </button>
              </span>
            </span>
          </div>

          {centreView === 'diagram' ? (
            <div className="wf-canvas">
              {diagram && diagram.nodes.length > 0 ? renderDiagram(diagram) : (
                <div className="wf-side-none">Nothing to draw yet — add a step.</div>
              )}
            </div>
          ) : (
            <div className="wf-step-list">
              {loading && <div className="wf-side-none">Loading…</div>}
              {!loading && steps.length === 0 && (
                <div className="wf-side-none">No steps yet — pick a node on the left to begin.</div>
              )}
              {steps.map((s, i) =>
                <StepRow key={s.key} s={s} depth={0} index={i} total={steps.length} />)}
            </div>
          )}

          {!readOnly && (
            <div className="wf-add">
              <span className="wf-add-hint">
                Pick a node on the left to add it, or
              </span>
              <button className="ca-btn" onClick={addBranch}>
                <i className="ti ti-git-branch" /> Add branch
              </button>
            </div>
          )}
        </section>
        {/* ── RIGHT · Node Configuration ──────────────────────────────────── */}
        <aside className="bl-card wf-props">
          <div className="wf-side-hdr">Node Configuration</div>
          {!selected && <div className="wf-side-none">Select a step to edit it.</div>}
          {selected && (
            <div className="wf-props-body">
              <div className="wf-props-title">
                <span className="wf-badge" style={{ background: CAT_BY_TYPE[selected.type]?.colour }}>
                  {CAT_BY_TYPE[selected.type]?.badge}
                </span>
                {CAT_BY_TYPE[selected.type]?.label}
              </div>

              {/* General / Rules / Execution — what a step IS, versus when it
                  fires. Tabs only appear for the sections that have fields. */}
              <div className="wf-tabs">
                {(['general', 'rules', 'execution'] as FieldTab[])
                  .filter(t => t === 'general' || fieldsForTab(CAT_BY_TYPE[selected.type], t).length > 0)
                  .map(t => (
                    <button key={t} type="button"
                      className={`wf-tab${propTab === t ? ' active' : ''}`}
                      onClick={() => setPropTab(t)}>
                      {t === 'general' ? 'General' : t === 'rules' ? 'Rules' : 'Execution'}
                    </button>
                  ))}
              </div>

              {propTab === 'general' && (
                <div className="wf-prow">
                  <label>Name <b className="wf-star">*</b></label>
                  <input className={`wf-in${!selected.name.trim() && !readOnly ? ' wf-req' : ''}`}
                    value={selected.name} readOnly={readOnly}
                    onChange={e => updateSelected({ name: e.target.value })} />
                </div>
              )}

              {fieldsForTab(CAT_BY_TYPE[selected.type], propTab).map(f => (
                <div className="wf-prow" key={f.key}>
                  {f.type !== 'bool' && (
                    <label>{f.label}{f.required && <b className="wf-star"> *</b>}</label>
                  )}
                  {renderField(f, selected)}
                </div>
              ))}

              {fieldsForTab(CAT_BY_TYPE[selected.type], propTab).length === 0 && propTab !== 'general' && (
                <div className="wf-side-none">Nothing to configure here for this component.</div>
              )}
            </div>
          )}
        </aside>
      </div>

      <ActionBar />
    </div>
  );
}
